import React from 'react';
import { Badge, Button, Table } from '@syneto/compass-react';
import moment from 'moment';
import Preloader from '../../../../components/preloader';
import {
	ADVISORY_STATUS,
	ADVISORY_STATUS_FILTERS,
	FIRMWARE_ADVISORIES_SIZE_PER_PAGE_LIST
} from '../../../../constants/firmware-advisories';
import { NOT_AVAILABLE } from '../../../../constants/hardware-inventory';
import {
	advisoryDisplayName,
	advisoryMatcherSummary,
	severityAppearance,
	statusAppearance
} from './firmwareAdvisoryDisplay';

const VISIBLE_VERSIONS = 3;

const FirmwareAdvisoriesTable = (props) => {
	const {
		advisories,
		pagination,
		componentTypes,
		isLoading,
		hasError,
		isSaving,
		status,
		onStatusChange,
		onPaginationChange,
		onAdd,
		onEdit,
		onRevoke
	} = props;

	/*
	 * Only pagination comes back through the table - the one filter the endpoint takes is the
	 * status, and it lives in the bar above so it can reset the offset at the same time.
	 */
	const handleTableChange = (type, { page, sizePerPage }) => {
		if (type !== 'pagination') {
			return;
		}

		const start = (page - 1) * sizePerPage;

		onPaginationChange(start >= 0 ? start : 0, sizePerPage);
	};

	const renderValue = (cell) => {
		return cell ?? NOT_AVAILABLE;
	};

	const renderComponentType = (cell, row) => {
		return advisoryDisplayName(row, componentTypes) || NOT_AVAILABLE;
	};

	const renderMatch = (cell, row) => {
		return <span className="text-muted">{advisoryMatcherSummary(row) || NOT_AVAILABLE}</span>;
	};

	/*
	 * An advisory can name a long list of versions and the column has to stay a column. The full
	 * list is on the element's title so nothing is actually hidden.
	 */
	const renderAffectedVersions = (cell) => {
		const versions = cell ?? [];

		if (versions.length === 0) {
			return NOT_AVAILABLE;
		}

		const shown = versions.slice(0, VISIBLE_VERSIONS).join(', ');
		const remaining = versions.length - VISIBLE_VERSIONS;

		return (
			<span title={versions.join(', ')}>
				{shown}{remaining > 0 ? ` +${remaining} more` : ''}
			</span>
		);
	};

	const renderBadge = (appearanceOf) => {
		return (cell) => {
			if (!cell) {
				return NOT_AVAILABLE;
			}

			return <Badge appearance={appearanceOf(cell)}>{cell}</Badge>;
		};
	};

	const renderUpdatedAt = (cell) => {
		return cell ? moment(cell).format('DD/MM/YYYY HH:mm') : NOT_AVAILABLE;
	};

	/*
	 * Revoking is one-way, so a revoked row simply has no action: an absent control is unambiguous
	 * where a disabled one invites "why?". Both buttons go dark while any write is in flight, which
	 * is what stops a second revoke racing the first against one list refresh.
	 */
	const renderActions = (cell, row) => {
		return (
			<>
				<Button role="tertiary" disabled={isSaving} onClick={() => { return onEdit(row); }}>Edit</Button>

				{row.status === ADVISORY_STATUS.ACTIVE && (
					<Button role="tertiary" disabled={isSaving} onClick={() => { return onRevoke(row); }}>Revoke</Button>
				)}
			</>
		);
	};

	/*
	 * A failed fetch leaves no rows, so the messages below would state a result the request never
	 * returned - an empty catalog, when in fact nothing loaded. The alert above carries the reason;
	 * here we only decline to draw a conclusion.
	 */
	const emptyTableState = () => {
		if (isLoading) {
			return <Preloader dataIsLoading={true} />;
		}

		if (hasError) {
			return 'Firmware advisories could not be loaded.';
		}

		if (status === ADVISORY_STATUS.REVOKED) {
			return 'No advisories have been revoked.';
		}

		if (status === ADVISORY_STATUS.ACTIVE) {
			return 'No active firmware advisories. Add one to start flagging firmware in the fleet.';
		}

		return 'No firmware advisories have been added yet.';
	};

	return (
		<div>
			<div className="d-flex justify-content-between align-items-center mb-3">
				<select
					className="form-select"
					style={{ maxWidth: '170px' }}
					value={status}
					onChange={(event) => { return onStatusChange(event.target.value); }}
				>
					{ADVISORY_STATUS_FILTERS.map((filter) => {
						return <option key={filter.label} value={filter.value}>{filter.label}</option>;
					})}
				</select>

				<Button onClick={() => { return onAdd(); }}>Add advisory</Button>
			</div>

			<Table
				data={advisories ?? []}
				keyField="id"
				remote
				pagination={{
					sizePerPageList: FIRMWARE_ADVISORIES_SIZE_PER_PAGE_LIST,
					sizePerPage: pagination?.itemsPerPage,
					showTotal: true,
					page: pagination?.currentPage,
					totalSize: pagination?.totalItems
				}}
				onTableChange={handleTableChange}
				noDataIndication={emptyTableState()}
			>
				<Table.Col field="componentTypeId" align="left" renderCell={renderComponentType}>
					Component type
				</Table.Col>
				<Table.Col field="category" align="left" renderCell={renderValue}>Category</Table.Col>
				<Table.Col field="match" align="left" renderCell={renderMatch}>Match</Table.Col>
				<Table.Col field="affectedVersions" align="left" renderCell={renderAffectedVersions}>
					Affected versions
				</Table.Col>
				<Table.Col field="targetVersion" align="left" renderCell={renderValue}>Target</Table.Col>
				<Table.Col field="severity" align="left" renderCell={renderBadge(severityAppearance)}>
					Severity
				</Table.Col>
				<Table.Col field="status" align="left" renderCell={renderBadge(statusAppearance)}>Status</Table.Col>
				<Table.Col field="updatedAt" align="left" renderCell={renderUpdatedAt}>Updated</Table.Col>
				<Table.Col field="actions" align="left" renderCell={renderActions}>Actions</Table.Col>
			</Table>
		</div>
	);
};

export default FirmwareAdvisoriesTable;
